"use client";

import { Button } from "@/components/ui/Button";
import { Bell } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { NotificationRow, groupByDay, isOpenAction } from "@/components/notification-ui";
import { showDesktopNotifications } from "@/lib/desktop-app";
import { getInbox, markNotificationRead, markAllNotificationsRead, type Inbox, type NotificationDTO } from "@/lib/actions/notifications";

const POLL_INTERVAL_MS = 45_000;

export function NotificationsMenu() {
  const router = useRouter();
  const [notifOpen, setNotifOpen] = useState(false);
  const [inbox, setInbox] = useState<Inbox>({ items: [], actionCount: 0, unreadCount: 0, hasMore: false });
  const notifRef = useRef<HTMLDivElement>(null);
  const { actionCount, unreadCount } = inbox;
  const actions = inbox.items.filter(isOpenAction);
  const rest = inbox.items.filter((n) => !isOpenAction(n));

  useEffect(() => {
    if (!notifOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [notifOpen]);

  // Fetch on mount, then poll on an interval — the unread badge should
  // update without a full page reload while the user stays signed in.
  useEffect(() => {
    let cancelled = false;
    function load() {
      getInbox({ take: 20 })
        .then((data) => {
          if (cancelled) return;
          setInbox(data);
          showDesktopNotifications(data.items).catch(() => undefined);
        })
        .catch((err) => console.error("Failed to load notifications:", err));
    }
    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  function handleNotificationClick(notification: NotificationDTO) {
    setNotifOpen(false);
    if (!notification.read) {
      setInbox((prev) => ({ ...prev, items: prev.items.map((n) => (n.id === notification.id ? { ...n, read: true } : n)) }));
      markNotificationRead(notification.id).catch((err) => console.error("Failed to mark notification read:", err));
    }
    if (notification.link) router.push(notification.link);
  }

  function handleMarkAllRead() {
    setInbox((prev) => ({ ...prev, unreadCount: 0, items: prev.items.map((n) => (n.actionKey === null ? { ...n, read: true } : n)) }));
    markAllNotificationsRead().catch((err) => console.error("Failed to mark all notifications read:", err));
  }

  const row = (n: NotificationDTO) => (
    <li key={n.id}>
      <NotificationRow n={n} compact onOpen={handleNotificationClick} onChanged={() => undefined} />
    </li>
  );
  const total = actionCount + unreadCount;

  return (
    <div className="relative" ref={notifRef}>
      <Button variant="ghost" iconOnly className="relative" onClick={() => setNotifOpen((o) => !o)} aria-label={`Notifications: ${actionCount} need action, ${unreadCount} unread`} title="Notifications">
        <Bell size={18} />
        {actionCount > 0 && (
          <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-xs font-medium leading-none text-primary-foreground">
            {actionCount > 9 ? "9+" : actionCount}
          </span>
        )}
        {unreadCount > 0 && (
          <span className="absolute bottom-0.5 right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-xs font-medium leading-none text-primary-foreground">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {notifOpen && (
        <div className="absolute right-0 top-full z-overlay mt-1 w-[min(22rem,calc(100vw-1rem))] overflow-hidden rounded-md border border-line bg-surface py-1 shadow-xl">
          <div className="flex items-center justify-between border-b border-line-subtle px-3 py-2 text-xs font-medium text-muted">
            <span>Notifications</span>
            {unreadCount > 0 && (
              <button type="button" onClick={handleMarkAllRead} className="text-accent hover:underline">
                Mark info read
              </button>
            )}
          </div>
          {total === 0 && inbox.items.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-subtle">No notifications yet</div>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {actions.length > 0 && (
                <li>
                  <div className="bg-warning-subtle px-3 py-1 text-xs font-bold uppercase tracking-wider text-warning-strong">Action required ({actionCount})</div>
                  <ul>{actions.map(row)}</ul>
                </li>
              )}
              {groupByDay(rest).map((group) => (
                <li key={group.label}>
                  <div className="bg-surface-muted px-3 py-1 text-xs font-bold uppercase tracking-wider text-subtle">{group.label}</div>
                  <ul>{group.items.map(row)}</ul>
                </li>
              ))}
            </ul>
          )}
          <Link
            href={actionCount > 0 ? "/notifications?tab=action" : "/notifications"}
            onClick={() => setNotifOpen(false)}
            className="block border-t border-line-subtle px-3 py-2 text-center text-xs font-medium text-muted hover:bg-surface-muted hover:text-fg"
          >
            {actionCount > 0 ? "Review what needs you" : "See all"}
          </Link>
        </div>
      )}
    </div>
  );
}
